import { retryableQuery } from '../../../functions/retryableQuery';
import { db } from '../../../db';
import { consultations } from '../models/consultations';
import { Consultation } from '../types/Consultation';
import { eq, and, or, desc, asc, inArray, lt, isNull, isNotNull } from 'drizzle-orm';
import { BaseRepository } from '../../../classes/BaseRepository';
import { convertToAppError, AppError, AsyncResult, err, ok } from '@shared/utils';
import { HTTP_STATUS_NAMES, DATA_API_MESSAGES, TIME_CONVERSION } from '@shared/constants';
import { CONSULTATION_STATUS } from '../constants/CONSULTATION_STATUS';

/**
 * @group Patient
 * @summary Consultation repository
 * 
 * <blockquote>
 * Provides data access methods for consultation management, including CRUD operations and consultation-based permission checks.
 * Extends BaseRepository to inherit common CRUD operations while adding domain-specific query methods for EHR integration.
 * </blockquote>
 * 
 * @example
 * ```typescript
 * const consultationRepository = new ConsultationRepository();
 * const consultation = await consultationRepository.getById('consultation-uuid');
 * const activeConsultations = await consultationRepository.getActiveConsultationsByUserId('user-uuid');
 * ```
 */
export class ConsultationRepository extends BaseRepository<Consultation> {
  protected table = consultations;

  /**
   * Get consultations by user ID with optional status filter
   * 
   * @param userId - The user ID to filter consultations
   * @param status - Optional consultation status filter
   * @returns AsyncResult containing consultations array or error
   */
  async getConsultationsByUserId(userId: string, status?: string): AsyncResult<Consultation[], AppError> {
    if (!userId) {
      return err(convertToAppError(
        'User ID is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() => {
          if (status) {
            return database.select().from(this.table)
              .where(and(eq(this.table.user_id, userId), eq(this.table.status, status)))
              .orderBy(desc(this.table.date_created));
          } else {
            return database.select().from(this.table)
              .where(eq(this.table.user_id, userId))
              .orderBy(desc(this.table.date_created));
          }
        });

        return result.match({
          ok: (rows) => ok(rows),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Get active consultations for a user (in_progress status)
   * 
   * @param userId - The user ID to filter consultations
   * @returns AsyncResult containing active consultations array or error
   */
  async getActiveConsultationsByUserId(userId: string): AsyncResult<Consultation[], AppError> {
    return this.getConsultationsByUserId(userId, CONSULTATION_STATUS.IN_PROGRESS);
  }

  /**
   * Get consultations assigned to a clinician (scheduled or in_progress)
   *
   * @param clinicianUserId - The clinician user ID to filter consultations
   * @returns AsyncResult containing consultations array or error
   */
  async getConsultationsByClinicianId(
    clinicianUserId: string
  ): AsyncResult<Consultation[], AppError> {
    if (!clinicianUserId) {
      return err(convertToAppError(
        'Clinician user ID is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database
            .select()
            .from(this.table)
            .where(
              and(
                eq(this.table.clinician_user_id, clinicianUserId),
                inArray(this.table.status, [
                  CONSULTATION_STATUS.SCHEDULED,
                  CONSULTATION_STATUS.IN_PROGRESS,
                ])
              )
            )
            .orderBy(desc(this.table.scheduled_for))
        );

        return result.match({
          ok: (rows) => ok(rows),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Get consultation by external ID (public UUID for room/reservation lookup)
   *
   * @param externalId - The external ID to search for
   * @returns AsyncResult containing consultation or null if not found
   */
  async getConsultationByExternalId(externalId: string): AsyncResult<Consultation | null, AppError> {
    if (!externalId) {
      return err(convertToAppError(
        'External ID is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.select().from(this.table).where(eq(this.table.external_id, externalId))
        );

        return result.match({
          ok: (rows) => ok(rows.length > 0 ? rows[0] : null),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Get consultation by EHR consultation ID
   * 
   * @param ehrConsultationId - The EHR consultation ID to search for
   * @returns AsyncResult containing consultation or error
   */
  async getConsultationByEhrId(ehrConsultationId: string): AsyncResult<Consultation | null, AppError> {
    if (!ehrConsultationId) {
      return err(convertToAppError(
        'EHR consultation ID is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.select().from(this.table).where(eq(this.table.ehr_consultation_id, ehrConsultationId))
        );

        return result.match({
          ok: (rows) => {
            if (rows.length === 0) {
              return ok(null);
            }
            return ok(rows[0]);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Start a consultation (update status to in_progress and set started_at)
   * 
   * @param consultationId - The consultation ID to start
   * @returns AsyncResult containing updated consultation or error
   */
  async startConsultation(consultationId: string): AsyncResult<Consultation, AppError> {
    if (!consultationId) {
      return err(convertToAppError(
        'Consultation ID is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.update(this.table)
            .set({
              status: CONSULTATION_STATUS.IN_PROGRESS,
              started_at: new Date(),
              date_modified: new Date()
            })
            .where(eq(this.table.id, consultationId))
            .returning()
        );

        return result.match({
          ok: (rows) => {
            if (rows.length === 0) {
              return err(convertToAppError(
                `Consultation not found with id: ${consultationId}`,
                DATA_API_MESSAGES.ERRORS.ID_REQUIRED,
                HTTP_STATUS_NAMES.NOT_FOUND
              ));
            }
            return ok(rows[0]);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * End a consultation (update status to completed and set ended_at)
   * 
   * @param consultationId - The consultation ID to end
   * @returns AsyncResult containing updated consultation or error
   */
  async endConsultation(consultationId: string): AsyncResult<Consultation, AppError> {
    if (!consultationId) {
      return err(convertToAppError(
        'Consultation ID is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.update(this.table)
            .set({
              status: CONSULTATION_STATUS.COMPLETED,
              ended_at: new Date(),
              date_modified: new Date()
            })
            .where(eq(this.table.id, consultationId))
            .returning()
        );

        return result.match({
          ok: (rows) => {
            if (rows.length === 0) {
              return err(convertToAppError(
                `Consultation not found with id: ${consultationId}`,
                DATA_API_MESSAGES.ERRORS.ID_REQUIRED,
                HTTP_STATUS_NAMES.NOT_FOUND
              ));
            }
            return ok(rows[0]);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Cancel a consultation (update status to cancelled)
   * 
   * @param consultationId - The consultation ID to cancel
   * @returns AsyncResult containing updated consultation or error
   */
  async cancelConsultation(consultationId: string): AsyncResult<Consultation, AppError> {
    if (!consultationId) {
      return err(convertToAppError(
        'Consultation ID is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.update(this.table)
            .set({
              status: CONSULTATION_STATUS.CANCELLED,
              date_modified: new Date()
            })
            .where(eq(this.table.id, consultationId))
            .returning()
        );

        return result.match({
          ok: (rows) => {
            if (rows.length === 0) {
              return err(convertToAppError(
                `Consultation not found with id: ${consultationId}`,
                DATA_API_MESSAGES.ERRORS.ID_REQUIRED,
                HTTP_STATUS_NAMES.NOT_FOUND
              ));
            }
            return ok(rows[0]);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Get all consultations with status='waiting' ordered by date_created ASC (FIFO).
   *
   * @returns AsyncResult containing waiting consultations array or error
   */
  async getWaitingConsultations(): AsyncResult<Consultation[], AppError> {
    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database
            .select()
            .from(this.table)
            .where(eq(this.table.status, CONSULTATION_STATUS.WAITING))
            .orderBy(asc(this.table.date_created))
        );

        return result.match({
          ok: (rows) => ok(rows),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Get 1-based queue position for a consultation (count of waiting consultations created before it + 1).
   *
   * @param consultationId - The consultation ID to get position for
   * @returns AsyncResult containing position (1-based) or error
   */
  async getQueuePosition(consultationId: string): AsyncResult<number, AppError> {
    if (!consultationId) {
      return err(convertToAppError(
        'Consultation ID is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    return db.match({
      ok: async (database) => {
        const targetResult = await retryableQuery(() =>
          database
            .select({ date_created: this.table.date_created })
            .from(this.table)
            .where(eq(this.table.id, consultationId))
        );

        if (targetResult.isErr) return err(targetResult.getFail());
        const targetRows = targetResult.getSuccess();
        const target = targetRows.length > 0 ? targetRows[0] : null;
        if (!target) {
          return err(convertToAppError(
            `Consultation not found with id: ${consultationId}`,
            DATA_API_MESSAGES.ERRORS.ID_REQUIRED,
            HTTP_STATUS_NAMES.NOT_FOUND
          ));
        }

        const countResult = await retryableQuery(() =>
          database
            .select()
            .from(this.table)
            .where(
              and(
                eq(this.table.status, CONSULTATION_STATUS.WAITING),
                lt(this.table.date_created, target.date_created)
              )
            )
        );

        return countResult.match({
          ok: (rows) => ok(rows.length + 1),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Atomically assign clinician and start consultation.

   * Only updates if status='waiting' (race-safe). Returns err if 0 rows affected.
   *
   * @param consultationId - The consultation ID to start
   * @param clinicianUserId - The clinician user ID to assign
   * @returns AsyncResult containing updated consultation or error
   */
  async assignClinicianAndStart(
    consultationId: string,
    clinicianUserId: string
  ): AsyncResult<Consultation, AppError> {
    if (!consultationId || !clinicianUserId) {
      return err(convertToAppError(
        'Consultation ID and clinician user ID are required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database
            .update(this.table)
            .set({
              clinician_user_id: clinicianUserId,
              status: CONSULTATION_STATUS.IN_PROGRESS,
              started_at: new Date(),
              date_modified: new Date()
            })
            .where(
              and(
                eq(this.table.id, consultationId),
                eq(this.table.status, CONSULTATION_STATUS.WAITING)
              )
            )
            .returning()
        );

        return result.match({
          ok: (rows) => {
            if (rows.length === 0) {
              return err(convertToAppError(
                `Consultation not found or no longer waiting: ${consultationId}`,
                DATA_API_MESSAGES.ERRORS.ID_REQUIRED,
                HTTP_STATUS_NAMES.CONFLICT
              ));
            }
            return ok(rows[0]);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Get active (in_progress) consultation for a clinician.
   *
   * @param clinicianUserId - The clinician user ID to look up
   * @returns AsyncResult containing consultation or null if none
   */
  async getActiveConsultationByClinicianId(
    clinicianUserId: string
  ): AsyncResult<Consultation | null, AppError> {
    if (!clinicianUserId) {
      return err(convertToAppError(
        'Clinician user ID is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database
            .select()
            .from(this.table)
            .where(
              and(
                eq(this.table.clinician_user_id, clinicianUserId),
                eq(this.table.status, CONSULTATION_STATUS.IN_PROGRESS)
              )
            )
            .limit(1)
        );

        return result.match({
          ok: (rows) => ok(rows.length > 0 ? rows[0] : null),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Update last_polled_at to now() for a consultation.
   *
   * @param consultationId - The consultation ID to update
   * @returns AsyncResult containing void or error
   */
  async updateLastPolledAt(consultationId: string): AsyncResult<void, AppError> {
    if (!consultationId) {
      return err(convertToAppError(
        'Consultation ID is required',
        DATA_API_MESSAGES.ERRORS.BAD_REQUEST,
        HTTP_STATUS_NAMES.BAD_REQUEST
      ));
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database
            .update(this.table)
            .set({ last_polled_at: new Date(), date_modified: new Date() })
            .where(eq(this.table.id, consultationId))
            .returning()
        );

        return result.match({
          ok: () => ok(undefined),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Cancel stale waiting consultations.

   * Cancels entries where (last_polled_at IS NOT NULL AND last_polled_at < threshold)
   * OR (last_polled_at IS NULL AND date_created < threshold). Includes never-polled fallback.
   *
   * @param staleThresholdSeconds - Seconds of inactivity before cancellation
   * @returns AsyncResult containing count of cancelled rows or error
   */
  async cancelStaleWaitingConsultations(
    staleThresholdSeconds: number
  ): AsyncResult<number, AppError> {
    const threshold = new Date(
      Date.now() - staleThresholdSeconds * TIME_CONVERSION.MILLISECONDS_PER_SECOND
    );

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database
            .update(this.table)
            .set({
              status: CONSULTATION_STATUS.CANCELLED,
              date_modified: new Date()
            })
            .where(
              and(
                eq(this.table.status, CONSULTATION_STATUS.WAITING),
                or(
                  and(
                    isNotNull(this.table.last_polled_at),
                    lt(this.table.last_polled_at, threshold)
                  ),
                  and(
                    isNull(this.table.last_polled_at),
                    lt(this.table.date_created, threshold)
                  )
                )
              )
            )
            .returning()
        );

        return result.match({
          ok: (rows) => ok(rows.length),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }
}
